import { FC, useEffect, useCallback, useMemo, useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import { makeSxStyles } from "ui";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import MapProvider from "../../../components/map/map-provider";
import MapComponent from "../../../components/map/map";
import {
  MapComponentProps,
  MapMarker,
  LassoMode,
} from "../../../components/map/map.types";
import useNotificationRecipientsStore from "../../../state/notification-recipients-store";
import NotificationRecipientsSidebar from "../components/notification-recipients-sidebar";
import NotificationRecipientsMapFilters from "../components/notification-recipients-map-filters";
import DashboardContentLayout from "../../../pages/dashboard/components/dashboard-content-layout";
import {
  useAdminLocationV2ControllerGetAllLocationsPaginated,
  useAdminLocationV2ControllerGetAllLocations,
  useAdminNotificationNotificationRecipientControllerGetAll,
  useAdminNotificationNotificationRecipientControllerCreate,
  useAdminNotificationNotificationRecipientControllerDelete,
  AdminNotificationNotificationRecipientControllerCreateResponse,
  AdminNotificationNotificationRecipientControllerDeleteResponse,
} from "service";
import { AdminAllLocationsResDto } from "service/api/estabrookSchemas";
import { AdRoutes } from "../../../constants/ad-routes";
import { useMapHeight } from "../../../shared/hooks/useMapHeight";
import { queryClient } from "../../../query-client";
import { queryKeyFn } from "service/api/estabrookContext";
import useUnsavedChangesBlocker from "../../../shared/hooks/useUnsavedChangesBlocker";
import Navbar from "../../../components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";

const NotificationRecipientsPage: FC = () => {
  const styles = useStyles();
  const { notificationId } = useParams<{ notificationId: string }>();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);

  const {
    addresses,
    selectedAddresses,
    drawing,
    lassoMode,
    mapZoom,
    mapCentering,
    query,
    isLoading,
    searchedAddresses,
    showAllCustomers,
    showFlatRateOnly,
    showOnDemandOnly,
    showSnowReporters,
    visibleRegions,
    setAddresses,
    setSelectedAddress,
    resetAllSelected,
    resetStore,
    toggleDrawing,
    setMapCentering,
    clearMapCentering,
    setMapZoom,
    setQuery,
    removeSelected,
    setSearchedAddresses,
  } = useNotificationRecipientsStore();

  useEffect(() => {
    resetStore();

    // Don't clear map centering during initial load to preserve user's current view
  }, [notificationId, resetStore]);

  const { data: recipientsData, isLoading: isLoadingRecipients } =
    useAdminNotificationNotificationRecipientControllerGetAll({
      pathParams: { notificationId: notificationId || "" },
    });

  useEffect(() => {
    if (recipientsData?.data) {
      setSelectedAddress(
        recipientsData.data.map((recipient) => ({
          id: recipient.locationId || `recipient-${recipient.customerId}`,
          address: recipient.address,
          customer: {
            id: recipient.customerId,
            firstName: recipient.firstName,
            lastName: recipient.lastName,
            serviceAccess: true,
            emailVerified: true,
          },
          selected: true,
        })),
        false,
        true, // isInitialLoad = true to prevent clearing map centering during initial load
      );

      // Don't clear map centering during initial load to preserve user's current view
      // Reset all flags when new data loads
    }
  }, [recipientsData]);

  const { mutateAsync: createRecipient } =
    useAdminNotificationNotificationRecipientControllerCreate();

  const { mutateAsync: deleteRecipient } =
    useAdminNotificationNotificationRecipientControllerDelete({
      onError: (error) => {
        console.error("Failed to remove recipient:", error);
        toast.error("Failed to remove recipient");
      },
    });

  const filteredAddresses = useMemo(() => {
    const filtered = addresses.filter((address) => {
      // 1. Address in region + region toggle OFF -> HIDE (region filter always applies)
      if (address.region && !visibleRegions.has(address.region.id)) {
        return false;
      }

      // 2. If all customer toggles are OFF -> HIDE all addresses
      if (!showFlatRateOnly && !showOnDemandOnly && !showSnowReporters) {
        return false;
      }

      // 3. INCLUSIVE LOGIC: Show address if it matches ANY enabled toggle
      // Show if flat rate toggle is ON and address is flat rate
      if (showFlatRateOnly && address.isFlatRate) {
        return true;
      }

      // Show if on demand toggle is ON and address is on demand
      if (showOnDemandOnly && address.isOnDemand) {
        return true;
      }

      // Show if snow reporters toggle is ON and address is snow reporter
      if (showSnowReporters && address.isSnowReporter) {
        return true;
      }

      // If none of the enabled toggles match this address, hide it
      return false;
    });

    return filtered;
  }, [
    addresses,
    showAllCustomers,
    showFlatRateOnly,
    showOnDemandOnly,
    showSnowReporters,
    visibleRegions,
  ]);

  // Note: Filters use inclusive logic - show addresses that match ANY enabled toggle
  // An address can be multiple types (e.g., flat rate AND snow reporter)
  // Selection logic will be handled by Quick Select in the sidebar

  // Fetch all locations across all pages automatically
  const { isLoading: isLoadingAddresses, data: addressesData } =
    useAdminLocationV2ControllerGetAllLocationsPaginated({
      queryParams: {
        page: 1,
        limit: 1000,
      },
    });

  const { isFetching: isFetchingSearchData, data: searchData } =
    useAdminLocationV2ControllerGetAllLocations(
      {
        queryParams: {
          search: query,
        },
      },
      {
        enabled: query.length > 3,
      },
    );

  const transformAddressesToMapMarkers = useCallback(
    (data: AdminAllLocationsResDto[]): MapMarker[] => {
      return data.map((location) => {
        // Customer type data is directly on location, not in customer object
        const locationType = location.type;
        const reportsEnabled = location.reportsEnabled;

        const isFlatRate = locationType === "annual";
        const isOnDemand = locationType === "onDemand";
        const isSnowReporter = reportsEnabled === true;

        return {
          id: location.id,
          latitude: location.latitude,
          longitude: location.longitude,
          address: location.address,
          locationSequence:
            typeof location.locationSequence === "number"
              ? location.locationSequence
              : location.locationSequence === null ||
                  location.locationSequence === undefined
                ? null
                : Number(location.locationSequence) || null,
          customer: location.customer,
          // Simple boolean flags
          isFlatRate,
          isOnDemand,
          isSnowReporter,
          region: location.region
            ? {
                ...location.region,
                color:
                  typeof location.region.color === "string"
                    ? location.region.color
                    : undefined,
              }
            : undefined,
          selected: false,
        };
      });
    },
    [],
  );

  useEffect(() => {
    if (addressesData?.data) {
      const mapMarkers = transformAddressesToMapMarkers(addressesData.data);
      setAddresses(mapMarkers, true); // isInitialLoad = true to preserve map centering

      // Don't clear map centering during initial load to preserve user's current view
    }
  }, [addressesData, transformAddressesToMapMarkers, setAddresses]);

  useEffect(() => {
    if (addresses.length > 0) {
      const store = useNotificationRecipientsStore.getState();
      const currentVisibleRegions = store.visibleRegions;

      // Only auto-initialize regions if this is the first load (no regions set yet)
      // and we haven't manually selected any regions yet
      if (currentVisibleRegions.size === 0) {
        const regionIds = new Set<string>();
        addresses.forEach((marker) => {
          if (marker.region) {
            regionIds.add(marker.region.id);
          }
        });

        // Auto-enable all regions only on initial load
        regionIds.forEach((regionId) => {
          store.toggleRegionVisibility(regionId);
        });

        // Don't clear map centering during initial load to preserve user's current view
      }
    }
  }, [addresses]);

  useEffect(() => {
    if (searchData?.data) {
      const mapMarkers = transformAddressesToMapMarkers(searchData.data);
      setSearchedAddresses(mapMarkers, false); // isInitialLoad = false since search is not initial load

      // Don't clear map centering during search to preserve user's current view
    } else {
      setSearchedAddresses([], false); // isInitialLoad = false since clearing search is not initial load

      // Don't clear map centering when search results are cleared to preserve user's current view
    }
  }, [searchData, transformAddressesToMapMarkers, setSearchedAddresses]);

  const invalidCustomersIds = selectedAddresses.filter(
    (addr) => !addr.customer?.emailVerified || !addr.customer?.serviceAccess,
  );
  const invalidCustomers = invalidCustomersIds.length;

  // Check if all selected addresses are invalid customers (no valid customers to save)
  const allSelectedAreInvalid =
    selectedAddresses.length > 0 &&
    invalidCustomers === selectedAddresses.length;

  const handleSaveRecipients = useCallback(async () => {
    if (!notificationId) return;

    setIsSaving(true);

    try {
      // Get current selected customer IDs
      const currentSelectedIds = new Set(
        selectedAddresses
          .filter((addr) => addr.customer)
          .map((addr) => {
            const locationId = addr.id.startsWith("recipient-")
              ? null
              : addr.id || null;
            return `${addr.customer!.id}:${locationId || ""}`;
          }),
      );

      // Find recipients to add (selected but not in initial)
      const toAdd = Array.from(currentSelectedIds)
        .filter(
          (id) =>
            !recipientsData?.data?.some(
              (recipient) =>
                recipient.customerId === id.split(":")[0] &&
                recipient.locationId === id.split(":")[1],
            ),
        )
        .filter(
          (id) =>
            !invalidCustomersIds.some(
              (invalidId) => invalidId.customer?.id === id.split(":")[0],
            ),
        );

      // Find recipients to remove (in initial but not selected)
      const toRemove = Array.from(
        recipientsData?.data?.map(
          (recipient) =>
            `${recipient.customerId}:${recipient.locationId || ""}`,
        ) || [],
      ).filter((id) => !currentSelectedIds.has(id));

      // Execute all operations
      const promises: Promise<
        | AdminNotificationNotificationRecipientControllerCreateResponse
        | AdminNotificationNotificationRecipientControllerDeleteResponse
      >[] = [];

      // Add new recipients only if there are any to add
      if (toAdd.length > 0) {
        promises.push(
          createRecipient({
            pathParams: { notificationId },
            body: {
              recipients: toAdd.map((id) => ({
                customerId: id.split(":")[0],
                locationId: id.split(":")[1] || undefined,
              })),
            },
          }),
        );
      }

      // Remove recipients - handle each recipient individually
      toRemove.forEach((recipientId) => {
        const [customerId, locationId] = recipientId.split(":");
        promises.push(
          deleteRecipient({
            pathParams: {
              notificationId,
              customerId,
            },
            queryParams: {
              locationId: locationId || undefined,
            },
          }),
        );
      });

      if (promises.length > 0) {
        await Promise.all(promises);
        const action =
          toAdd.length && toRemove.length
            ? "Updated"
            : toRemove.length
              ? "Removed"
              : "Added";
        toast.success(`Recipients ${action} successfully`);

        // Optimistically update the recipient count in the notifications cache
        // If this fails, we'll fall back to invalidating the cache
        try {
          const notificationsQueryKey = queryKeyFn({
            path: "/api/v1/admin/notifications",
            operationId: "adminNotificationControllerGetAll",
            variables: {},
          });

          queryClient.setQueriesData(
            notificationsQueryKey,
            (oldData: unknown) => {
              if (
                !oldData ||
                typeof oldData !== "object" ||
                !("data" in oldData)
              ) {
                return oldData;
              }

              const typedOldData = oldData as { data: unknown };

              // Check if data is an array
              if (!Array.isArray(typedOldData.data)) {
                return oldData;
              }

              const dataArray = typedOldData.data as Array<{
                id: string;
                recipientCount: number;
                [key: string]: unknown;
              }>;
              const newRecipientCount = selectedAddresses.filter(
                (addr) => addr.customer,
              ).length;

              return {
                ...typedOldData,
                data: dataArray.map((notification) =>
                  notification.id === notificationId
                    ? { ...notification, recipientCount: newRecipientCount }
                    : notification,
                ),
              };
            },
          );
        } catch (error) {
          console.error(
            "Failed to optimistically update cache, invalidating instead:",
            error,
          );
        }

        // Invalidate related notification endpoints to ensure fresh data on next visit
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v1/admin/notifications",
            operationId: "adminNotificationControllerGetAll",
            variables: {},
          }),
        );

        // Invalidate the specific notification details endpoint
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v1/admin/notifications/{notificationId}",
            operationId: "adminNotificationControllerGetOne",
            variables: { pathParams: { notificationId } },
          }),
        );

        // Invalidate the notification recipients endpoint for this notification
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v1/admin/notifications/{notificationId}/notification-recipients",
            operationId:
              "adminNotificationNotificationRecipientControllerGetAll",
            variables: { pathParams: { notificationId } },
          }),
        );
      }

      // Always navigate to notification details page after save, regardless of whether there were changes
      navigate(
        AdRoutes.notificationDetails.replace(":notificationId", notificationId),
      );

      // Clear map centering after save to prevent unwanted zooming
      clearMapCentering();
    } catch (error) {
      console.error("Failed to save recipients:", error);
      toast.error("Failed to save recipients");
    } finally {
      setIsSaving(false);
    }
  }, [
    notificationId,
    selectedAddresses,
    recipientsData?.data,
    invalidCustomersIds,
    createRecipient,
    deleteRecipient,
    navigate,
    clearMapCentering,
  ]);

  const handleMarkerClick = useCallback(
    (marker: MapMarker, isShiftPressed: boolean) => {
      const address = addresses.find((a) => a.id === marker.id);
      if (address) {
        const isCurrentlySelected = selectedAddresses.some(
          (selected) => selected.id === marker.id,
        );

        setSelectedAddress([address], isShiftPressed, false); // isInitialLoad = false since this is a user interaction

        // Only center the map if we're selecting a new marker or if it's a multi-select
        // Don't center if we're just clicking on an already selected marker
        if (!isCurrentlySelected || isShiftPressed) {
          setMapCentering(undefined, [address]);
        }
      }
    },
    [addresses, selectedAddresses, setSelectedAddress, setMapCentering],
  );

  const handleSelectedMarkersChange = useCallback(
    (selectedMarkers: MapMarker[]) => {
      const validSelectedMarkers = selectedMarkers.filter(
        (marker) => marker && marker.id && marker.customer,
      );

      setSelectedAddress(validSelectedMarkers, false, false); // isInitialLoad = false since this is a user interaction

      // Set map centering to zoom into selected addresses when using lasso tool
      if (validSelectedMarkers.length > 0) {
        setMapCentering(undefined, validSelectedMarkers);
      }
    },
    [setSelectedAddress, setMapCentering],
  );

  const { mapHeight } = useMapHeight();

  const mapProps: MapComponentProps = {
    mapWidth: "100%",
    mapHeight,
    markers: filteredAddresses,
    mapZoom,
    mapCentering,
    drawing,
    query,
    isLoading: isLoadingAddresses || isLoadingRecipients,
    isSearchLoading: isFetchingSearchData,
    searchedAddresses:
      searchedAddresses.length > 0 ? searchedAddresses : undefined,
    selectedAddresses,
    setQuery,
    onDrawingToggle: (isDrawing: boolean, mode?: LassoMode) =>
      toggleDrawing(isDrawing, mode),
    lassoMode,
    onMapZoomChange: setMapZoom,
    setSelectedAddress,
    setMapCentering,
    onMarkerClick: handleMarkerClick,
    onSelectedMarkersChange: handleSelectedMarkersChange,
    onResetSelection: resetAllSelected,
    mapFilters: <NotificationRecipientsMapFilters />,
  };

  const hasUnsavedChanges = useMemo(() => {
    // If we're currently saving, don't block navigation
    if (isSaving) return false;

    // Only consider valid customers when checking for changes
    // Invalid customers (unverified email or no service access) won't be saved anyway
    const currentSelectedIds = new Set(
      selectedAddresses
        .filter(
          (addr) =>
            addr.customer?.emailVerified && addr.customer?.serviceAccess,
        )
        .map((addr) => {
          const locationId = addr.id.startsWith("recipient-")
            ? null
            : addr.id || null;
          return `${addr.customer!.id}:${locationId || ""}`;
        }),
    );

    const existingRecipientIds = new Set(
      recipientsData?.data?.map(
        (recipient) => `${recipient.customerId}:${recipient.locationId || ""}`,
      ) || [],
    );

    // If sizes are different, there are changes
    if (currentSelectedIds.size !== existingRecipientIds.size) {
      return true;
    }

    // Check if there are any new addresses that don't exist in recipients
    for (const id of currentSelectedIds) {
      if (!existingRecipientIds.has(id)) {
        return true;
      }
    }

    return false;
  }, [selectedAddresses, recipientsData?.data, isSaving]);
  useUnsavedChangesBlocker(hasUnsavedChanges);

  return (
    <DashboardContentLayout loading={isLoading}>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Notification Center",
                url: AdRoutes.notifications,
              },
              {
                label: "Notification",
                url: AdRoutes.notificationDetails.replace(
                  ":notificationId",
                  notificationId || "",
                ),
              },
              {
                label: "Notifications Recipients",
              },
            ]}
          />
          <HeaderItemsRight>
            <Button
              variant="contained"
              onClick={handleSaveRecipients}
              disabled={isSaving || allSelectedAreInvalid || !hasUnsavedChanges}
              data-testid="save-recipients-button"
              startIcon={
                isSaving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : undefined
              }
            >
              {isSaving ? "Saving..." : "Save Recipients"}
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      <Box sx={styles.container}>
        <Box sx={styles.mapContainer}>
          <MapProvider>
            <MapComponent
              {...mapProps}
              existingSelection={(recipientsData?.data?.length || 0) > 0}
            />
          </MapProvider>
        </Box>

        <NotificationRecipientsSidebar
          selectedAddresses={selectedAddresses}
          removeSelected={removeSelected}
          onResetSelection={resetAllSelected}
          invalidCustomers={invalidCustomers}
        />
      </Box>
    </DashboardContentLayout>
  );
};

const useStyles = makeSxStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "row",
    height: "100%",
    width: "100%",
  },
  mapContainer: {
    flex: 1,
    height: "100%",
    minWidth: 0,
  },
}));

export default NotificationRecipientsPage;
